import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {makeAutoObservable} from "mobx";
import {ModalType, TeamRegion} from "data/enums";
import type {IModalMessage, TModalArguments} from "data/types/modals";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {Bindings} from "data/constants/bindings";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IUser, IUserStore} from "data/stores/user/user.store";
import type {ITeamStore} from "data/stores/team/team.store";
import {compact, size} from "lodash";

export interface IModalsController extends ViewController {
	show: (...args: TModalArguments) => void;
	close: () => void;
	setIsTeamSkipped: () => void;
	onWelcomeCtaClick: () => void;
	get user(): IUser | undefined;
	get visibleModal(): ModalType | null;
	get modalContent(): IModalMessage | null;
	get i18n(): ILocalizationStore;
	get selectedRegion(): TeamRegion;
}

@injectable()
export class ModalsController implements IModalsController {
	constructor(
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.TeamStore) private _teamStore: ITeamStore,
		@inject(Bindings.LocalizationStore) public i18n: ILocalizationStore
	) {
		makeAutoObservable(this);
	}

	get selectedRegion() {
		return this._teamStore.selectedRegion;
	}

	get user(): IUser | undefined {
		return this._userStore.user;
	}

	get visibleModal() {
		return this._modalsStore.modal;
	}

	get modalContent() {
		return this._modalsStore.modalContent;
	}

	show = (...args: TModalArguments) => {
		this._modalsStore.showModal(...args);
	};

	close = () => this._modalsStore.hideModal();

	setIsTeamSkipped = () => {
		const isFullEuLineup = size(compact(this._teamStore.euLineup)) === 12;
		const isFullUsLineup = size(compact(this._teamStore.usLineup)) === 12;
		this._teamStore.setIsSkipActive();
		this._teamStore.setIsFinalSaved();
		this._teamStore.setIsTeamSkipped();
		this._teamStore.clearLineup();

		if (this._userStore.isAuthorized) {
			if (isFullEuLineup) {
				this._teamStore.setEuTeamSaved();
			}
			if (isFullUsLineup) {
				this._teamStore.setUsTeamSaved();
			}
			this.close();
		} else {
			localStorage.setItem("euLineup", JSON.stringify(this._teamStore.euLineup));
			localStorage.setItem("usLineup", JSON.stringify(this._teamStore.usLineup));
			localStorage.setItem("isSkipped", "1");

			this.close();
			this._teamStore.clearIsPromptActive();

			this._teamStore.setRegionTeamSaved();

			// this.show(ModalType.LOSE_PICKS, {
			// 	message: "",
			// });
		}
	};

	onWelcomeCtaClick = () => {
		this.close();
	};

	dispose(): void {
		return;
	}

	init(): void {
		return;
	}
}
